package fr.frux.feature.map

import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.outlined.FormatListBulleted
import androidx.compose.material.icons.outlined.MyLocation
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.unit.dp
import fr.frux.feature.map.resources.Res
import fr.frux.feature.map.resources.map_action_list_description
import fr.frux.feature.map.resources.map_action_locate_me_description
import fr.frux.feature.map.resources.map_action_locating_description
import org.jetbrains.compose.resources.stringResource

/** Empreinte commune aux actions flottantes de la carte, pour que leur colonne s'aligne. */
internal val MAP_ACTION_SIZE = 56.dp

/**
 * Bouton « me localiser », point de rentree apres un refus (#117).
 *
 * Reprend l'empreinte du FAB « ajouter » qu'il surmonte — meme taille, meme rayon — pour que la
 * colonne d'actions se lise comme une pile alignee et non comme deux boutons sans rapport. Seule
 * la couleur les separe, et c'est elle qui porte la hierarchie : accent pour l'action primaire,
 * surface pour ce controle de carte. `contentDescription` obligatoire : l'element est purement
 * iconographique.
 *
 * Pendant [isLocating], l'icone cede la place a un indicateur et le bouton devient inerte : un
 * fix frais prend plusieurs secondes, et sans retour l'utilisateur retape (#128). Le
 * `contentDescription` change avec lui, sinon un lecteur d'ecran annoncerait toujours l'action au
 * lieu de l'attente.
 */
@Composable
internal fun LocateMeButton(
    isLocating: Boolean,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        onClick = onClick,
        enabled = !isLocating,
        modifier = modifier.size(MAP_ACTION_SIZE),
        // Cercle, pas `shapes.large` : ce token est un rayon FIXE (20 dp), donc un squircle sur
        // 56 dp et un cercle parfait sur les 40 dp du bouton legende juste au-dessus. Deux formes
        // differentes dans la meme colonne. Le cercle aligne aussi ces controles sur le reste des
        // surfaces flottantes de l'ecran (pastille de recherche, filtres, « + » de la nav).
        shape = CircleShape,
        color = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.primary,
        shadowElevation = 2.dp,
    ) {
        Box(contentAlignment = Alignment.Center) {
            if (isLocating) {
                val locatingDescription = stringResource(Res.string.map_action_locating_description)
                CircularProgressIndicator(
                    modifier =
                        Modifier
                            .size(LOCATE_PROGRESS_SIZE)
                            .semantics { contentDescription = locatingDescription },
                    strokeWidth = 2.dp,
                    color = MaterialTheme.colorScheme.primary,
                )
            } else {
                Icon(
                    imageVector = Icons.Outlined.MyLocation,
                    contentDescription = stringResource(Res.string.map_action_locate_me_description),
                )
            }
        }
    }
}

/**
 * Bouton « liste » de la colonne d'actions (#263). Meme empreinte que « me localiser » et non
 * celle, reduite, du bouton legende : ouvrir la liste des arbres est une action de consultation a
 * part entiere, pas une aide contextuelle.
 *
 * Il remplace la bande permanente que la feuille « autour de toi » occupait en bas de l'ecran :
 * la liste ne coute plus rien tant qu'on ne la demande pas, mais il lui faut alors une commande
 * visible — sans elle, elle ne serait atteignable par aucun geste decouvrable.
 */
@Composable
internal fun MapListButton(
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
) {
    Surface(
        onClick = onClick,
        modifier = modifier.size(MAP_ACTION_SIZE),
        shape = CircleShape,
        color = MaterialTheme.colorScheme.surface,
        contentColor = MaterialTheme.colorScheme.primary,
        shadowElevation = 2.dp,
    ) {
        Box(contentAlignment = Alignment.Center) {
            Icon(
                imageVector = Icons.AutoMirrored.Outlined.FormatListBulleted,
                contentDescription = stringResource(Res.string.map_action_list_description),
            )
        }
    }
}

/** Diametre de l'indicateur d'attente : sous l'empreinte de l'icone qu'il remplace. */
private val LOCATE_PROGRESS_SIZE = 20.dp
